/** @format */

import { ParentGroupSummary } from "@question-bank/shared";
import { useTranslation } from "react-i18next";
import { QuestionCard, QuestionCardSkeleton } from "./QuestionCard";
import { PaperEntry } from "../utils/paper";
import { useBrand } from "../hooks/useBrand";
import { usePapersWithAttachments } from "../hooks/usePapersWithAttachments";
import { hasAdditionalResources } from "../utils/attachments";
import React from "react";

interface SearchResultsProps {
	isLoading?: boolean;
	error?: string | null;
	results?: ParentGroupSummary[];
	onRetry?: () => void;
	onClearFilters?: () => void;
	onAddQuestion?: (question: ParentGroupSummary) => void;
	onRemoveQuestion?: (question: ParentGroupSummary) => void;
	addedQuestions?: PaperEntry[];
	pagination?: React.ReactNode;
}

const textClassName = "text-lg text-(--public-color-text-default) font-roboto";

export const SearchResults = ({
	isLoading,
	error,
	results,
	onRetry,
	onClearFilters,
	onAddQuestion,
	onRemoveQuestion,
	addedQuestions,
	pagination,
}: SearchResultsProps) => {
	const { t } = useTranslation();
	const { brand } = useBrand();
	const paperIdsWithAttachments = usePapersWithAttachments(brand);

	const isQuestionAdded = (question: ParentGroupSummary) => {
		return addedQuestions?.some((q) => q.parentQuestionId === question.parentQuestionId) ?? false;
	};

	if (isLoading) {
		return (
			<>
				<p role="status" aria-live="polite" className="mt-2 text-sm font-roboto text-(--public-color-text-lighter)">
					{t("searchResults.searching")}
				</p>
				<div className="grid gap-4 mt-2 grid-cols-[repeat(auto-fill,minmax(min(22rem,100%),1fr))]">
					{Array.from({ length: 6 }, (_, i) => (
						<QuestionCardSkeleton key={i} />
					))}
				</div>
			</>
		);
	}
	if (error) {
		return (
			<div className="flex flex-col items-center justify-center h-full gap-3 mt-2 ">
				<p className={textClassName}>{t("searchResults.failToFetch")}</p>
				<div>
					<button
						onClick={onRetry}
						type="button"
						className="border rounded-md bg-red-600 text-white cursor-pointer w-24 h-8 font-medium font-roboto"
					>
						{t("common.retry")}
					</button>
				</div>
			</div>
		);
	}
	if (!results || results.length === 0) {
		return (
			<div className="flex flex-col w-full items-center justify-center pt-20 text-lg font-medium">
				<p className={textClassName}>{t("searchResults.emptyResults")}</p>
				<div>
					<button
						onClick={onClearFilters}
						type="button"
						className="border rounded-md bg-red-600 text-white cursor-pointer w-32 h-8 font-medium font-roboto"
					>
						{t("common.clearFilters")}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="pr-1.25 mt-2">
			<div
				className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(22rem,100%),1fr))]"
				data-testid="search-results"
			>
				{results.map((question) => (
					<QuestionCard
						question={question}
						key={question.parentQuestionId}
						onAdd={onAddQuestion}
						isAdded={isQuestionAdded(question)}
						onRemove={onRemoveQuestion}
						hasResources={hasAdditionalResources(question, paperIdsWithAttachments)}
					/>
				))}
			</div>
			{pagination && <div className="flex justify-center mt-2">{pagination}</div>}
		</div>
	);
};
